import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBText } from "..";
import {
  View,
  TouchableOpacity,
  Modal,
  FlatList,
  Dimensions,
} from "react-native";
import { useState, useRef } from "react";
import { VDBIcons } from "@utils/VDBIcons";
import styled from "styled-components/native";
import LineViewContainer from "@scenes/requests/utils/LineView";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { useTranslation } from "react-i18next";
import { DefaultTheme } from "styled-components/native";

export type CustomSelectProps = {
  title: string;
  options: { label: string; value: string; isDisabled?: boolean }[];
  onSelect: (value: string) => void;
  defaultValue?: string;
};

const DropDown = ({
  options,
  defaultValue,
  onSelect,
  title,
}: CustomSelectProps) => {
  const { theme, typography } = useScreenOptionsHooks();
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const triggerRef = useRef<View>(null);
  const { t } = useTranslation();

  const handleSelect = (value: string) => {
    onSelect(value);
    setSelectedValue(value);
    setDropdownVisible(false);
  };

  const openDropdown = () => {
    if (triggerRef.current) {
      triggerRef.current.measure((fx, fy, width, height, px, py) => {
        setDropdownPosition({
          top: py + height,
          left: px,
          width,
        });
        setDropdownVisible(true);
      });
    }
  };

  return (
    <Container>
      <TriggerButton ref={triggerRef} onPress={openDropdown} theme={theme}>
        <View
          style={{
            flexDirection: "column",
            gap: 4,
            flexShrink: 1,
            flexGrow: 1,
            justifyContent: "center",
          }}
        >
          {!!title && (
            <VDBText style={{ padding: 0, margin: 0 }}>{title}</VDBText>
          )}
          <VDBText color={theme["core-05"]} type={typography["Body-SB"]}>
            {options.find((option) => option.value === selectedValue)?.label ||
              "Select option"}
          </VDBText>
        </View>
        <VDBIcon
          size={24}
          color={theme["main-01"]}
          variant={IconsVariant.VDBCustom}
          name={
            isDropdownVisible
              ? VDBIcons.FILLED_CIRCLE_UP
              : VDBIcons.FILLED_CIRCLE_DOWN
          }
        />
      </TriggerButton>

      <Modal
        visible={isDropdownVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setDropdownVisible(false)}
      >
        <TouchableOpacity
          style={{ flex: 1 }}
          activeOpacity={1}
          onPress={() => setDropdownVisible(false)}
        >
          {dropdownPosition && (
            <DropdownContainer position={dropdownPosition} theme={theme}>
              <FlatList
                data={options}
                keyExtractor={(item) => item.value}
                renderItem={({ item, index }) => (
                  <>
                    <OptionButton
                      onPress={() => handleSelect(item.value)}
                      isSelected={item.value === selectedValue}
                      isDisabled={item.isDisabled || false}
                      theme={theme}
                    >
                      <VDBText
                        color={theme["core-05"]}
                        type={typography["Body-M"]}
                      >
                        {t(item?.label)}
                      </VDBText>
                    </OptionButton>
                    {index < options.length - 1 && (
                      <LineViewContainer background="core-03" />
                    )}
                  </>
                )}
              />
            </DropdownContainer>
          )}
        </TouchableOpacity>
      </Modal>
    </Container>
  );
};

const Container = styled.View``;

const TriggerButton = styled.TouchableOpacity<{ theme: any }>`
  height: auto;
  padding: 10px;
  border-radius: 12px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-color: ${(props: { theme: DefaultTheme }) => props.theme["core-06"]};
  border-width: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
`;

const DropdownContainer = styled.View<{
  position: { top: number; left: number; width: number };
}>`
  position: absolute;
  top: ${(props: { position: { top: number } }) => props.position.top}px;
  left: ${(props: { position: { left: number } }) => props.position.left}px;
  width: ${(props: { position: { width: number } }) => props.position.width}px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 10px;
  box-shadow: 0px 2px 3.84px rgba(0, 0, 0, 0.3);
  elevation: 5;
  max-height: ${Dimensions.get("window").height * 0.4}px;
  overflow: hidden;
`;

const OptionButton = styled.TouchableOpacity<{
  isSelected: boolean;
  isDisabled: boolean;
  theme: any;
}>`
  padding: 8px;
  background-color: ${(props: {
    isSelected: boolean;
    isDisabled: boolean;
    theme: DefaultTheme;
  }) => (props.isSelected ? props.theme["core-02"] : "transparent")};
  opacity: ${(props: any) => (props.isDisabled ? 0.5 : 1)};
  height: 44px;
  justify-content: center;
`;

export default DropDown;
